import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import useApi from "../hooks/useApi";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Star,
  Clock,
  Users,
  Calendar,
  ChevronDown,
  ChevronUp,
  User,
} from "lucide-react";
import logo from "../images/logo.png";
import SubscriptionModal from './SubscriptionModal';


const CourseLandingPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loginWithRedirect, logout, user } = useAuth0();
  const [course, setCourse] = useState(null);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedModule, setExpandedModule] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);
  
  // New state for subscription modal
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionEmail, setSubscriptionEmail] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  const { fetchCourseDetail, checkEnrollmentStatus, createCheckoutSession, subscribeToDripCourse } =
    useApi();

  useEffect(() => {
    const loadCourseAndEnrollmentStatus = async () => {
      try {
        const fetchedCourse = await fetchCourseDetail(id);
        const courseWithDripFlag = {
          ...fetchedCourse,
          isDripCourse: fetchedCourse.courseType === 'drip' || id === 'ai-agent-100'
        };
        setCourse(courseWithDripFlag);

        if (isAuthenticated) {
          const enrollmentStatus = await checkEnrollmentStatus(id);
          setIsEnrolled(enrollmentStatus);
        } else {
          setIsEnrolled(false);
        }
      } catch (err) {
        console.error("Error loading course details or enrollment status:", err);
        setError("Failed to load course details. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    loadCourseAndEnrollmentStatus();
  }, [id, isAuthenticated, fetchCourseDetail, checkEnrollmentStatus]);

  const handleEnroll = async () => {
    if (course.isDripCourse) {
//      setShowSubscriptionModal(true);
        navigate(`/courses/${id}/subscribe`);
    } else {
      if (!isAuthenticated) {
        loginWithRedirect({
          appState: { returnTo: `/courses/${id}` }
        });
        return;
      }

      setEnrolling(true);
      try {
        const { sessionId } = await createCheckoutSession(id);
        const stripe = window.Stripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
        await stripe.redirectToCheckout({ sessionId });
      } catch (error) {
        console.error('Error during enrollment:', error);
        setError('Failed to enroll in the course. Please try again.');
      } finally {
        setEnrolling(false);
      }
    }
  };

const handleSubscriptionSubmit = async (e) => {
  e.preventDefault();
  try {
    const result = await subscribeToDripCourse(id, subscriptionEmail);
    setSubscriptionStatus(result);

    if (result.status === 'existing_user') {
      if (result.hasSubscription) {
        loginWithRedirect({
          appState: { returnTo: `/courses/${id}/drip-content` }
        });
      } else {
        loginWithRedirect({
          appState: { returnTo: `/courses/${id}/drip-content` }
        });
      }
    }
    // For new users, the modal will show success message
  } catch (error) {
    console.error('Subscription error:', error);
    setSubscriptionStatus({ error: 'Failed to subscribe. Please try again.' });
  }
};


  const toggleModule = (index) => {
    setExpandedModule(expandedModule === index ? null : index);
  };

  const toggleProfileMenu = () => {
    setIsProfileMenuOpen(!isProfileMenuOpen);
  };

  if (loading) return <div className="text-center text-white">Loading course details...</div>;
  if (error) return <div className="text-center text-red-500">{error}</div>;
  if (!course) return <div className="text-center text-white">Course not found.</div>;

  return (
    <div className="bg-pink-50 min-h-screen text-gray-600">
      <header className="bg-transparent shadow-md sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center">
            <img src={logo} alt="AI Guru Logo" className="h-8 w-auto mr-2" />
          </Link>
          <nav className="flex items-center space-x-4">
            {isAuthenticated ? (
              <div className="relative" ref={profileMenuRef}>
                <button
                  onClick={toggleProfileMenu}
                  className="flex items-center focus:outline-none"
                >
                  {user.picture ? (
                    <img
                      src={user.picture}
                      alt={user.name}
                      className="h-8 w-8 rounded-full"
                    />
                  ) : (
                    <User className="h-8 w-8 p-1 bg-gray-300 rounded-full text-gray-700" />
                  )}
                </button>
                {isProfileMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10">
                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsProfileMenuOpen(false)}
                    >
                      Profile
                    </Link>
                    <button
                      onClick={() => {
                        setIsProfileMenuOpen(false);
                        logout({ returnTo: window.location.origin });
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => loginWithRedirect()}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded hover:bg-indigo-700 transition duration-300"
              >
                Log In
              </button>
            )}
          </nav>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            <h1 className="text-4xl font-bold mb-4">{course.title}</h1>
            
            {course.isDripCourse && (
              <div className="bg-purple-100 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold text-purple-800 mb-2">Daily Lessons Course</h3>
                <p className="text-purple-700">
                  New content unlocked per schedule. Start with a 1-day free trial!
                </p>
                <ul className="mt-2 text-purple-600 text-sm">
                  <li>• Access to initial lessons immediately</li>
                  <li>• New lessons unlocked every 48 hours</li>
                  <li>• Full support during trial period</li>
                </ul>
              </div>
            )}

            <div className="mb-6">
              <img
                src={course.thumbnail}
                alt={course.title}
                className="w-full rounded-lg"
              />
            </div>
            <p className="text-xl mb-6">{course.description}</p>

            <div className="flex items-center mb-6 text-black-300">
              <Star className="text-yellow-400 mr-1" />
              <span>
                {course.rating} ({course.reviewCount} reviews)
              </span>
              <Users className="ml-4 mr-1" />
              <span>{course.enrollmentCount} students enrolled</span>
              <Calendar className="ml-4 mr-1" />
              <span>
                Last updated {new Date(course.lastUpdated).toLocaleDateString()}
              </span>
            </div>

            <div className="mb-8">
              <video width="100%" height="315" controls>
                <source src={course.promoVideoURL} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">What you'll learn</h2>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {course.learningObjectives.map((objective, index) => (
                  <li key={index} className="flex items-start">
                    <svg
                      className="w-6 h-6 mr-2 text-green-500 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      ></path>
                    </svg>
                    <span>{objective}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">Course Content</h2>
              {course.sections.map((section, index) => (
                <div
                  key={index}
                  className="mb-4 border border-gray-700 rounded"
                >
                  <button
                    className="flex justify-between items-center w-full p-4 text-left bg-fuchsia-950 text-white hover:bg-fuchsia-600 transition-colors"
                    onClick={() => toggleModule(index)}
                  >
                    <span className="font-semibold">{section.title}</span>
                    {expandedModule === index ? (
                      <ChevronUp />
                    ) : (
                      <ChevronDown />
                    )}
                  </button>
                  {expandedModule === index && (
                    <ul className="p-4 bg-gray-800 text-white">
                      {section.lectures.map((lecture, lectureIndex) => (
                        <li key={lectureIndex} className="mb-2 flex items-center">
                          <Clock className="mr-2 w-4 h-4" />
                          <span>
                            {lecture.title} ({lecture.duration})
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">Prerequisites</h2>
              <ul className="list-disc pl-5">
                {course.prerequisites.map((prerequisite, index) => (
                  <li key={index}>{prerequisite}</li>
                ))}
              </ul>
            </div>

            {/* Certificate Section */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">Certificate</h2>
              <div className="flex items-center">
                <div className="relative w-full md:w-1/2">
                  <img
                    src="https://aigurupublic.s3.amazonaws.com/certificateBg.png"
                    alt="Course Certificate"
                    className="w-full rounded-lg"
                  />
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-black font-bold text-xl text-center">
                    {course.title}
                  </div>
                </div>
                <div className="w-full md:w-1/2 pl-4">
                  <p className="text-lg">
                    Upon completing the course, you will receive a certificate
                    with your name and course title:{" "}
                    <strong>"{course.title}"</strong>.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="md:col-span-1">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-900 text-black p-6 rounded-lg shadow-lg sticky top-8">
              <div className="text-4xl font-bold mb-4 text-white">
                {course.currency} {course.price}
                {course.isDripCourse && <span className="text-sm ml-2">(after trial)</span>}
              </div>
              {isEnrolled ? (
                <button
                  onClick={() => navigate(`/courses/${id}/content`)}
                  className="w-full bg-green-500 text-white py-3 rounded-lg font-semibold hover:bg-green-600 transition duration-300"
                >
                  Go to Course Content
                </button>
              ) : (
                <button
                  className="w-full bg-gray-300 text-indigo-900 py-3 rounded-lg font-semibold hover:bg-gray-400 transition duration-300"
                  onClick={handleEnroll}
                  disabled={enrolling}
                >
                  {enrolling ? 'Processing...' : course.isDripCourse ? 'Start Free Trial' : 'Enroll Now'}
                </button>
              )}

              {course.isDripCourse && (
                <p className="text-sm text-white mt-4">
                  1-day free trial, then {course.currency} {course.price}/month
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
<SubscriptionModal
  isOpen={showSubscriptionModal}
  onClose={() => {
    setShowSubscriptionModal(false);
    setSubscriptionStatus(null);
    setSubscriptionEmail('');
  }}
  subscriptionEmail={subscriptionEmail}
  setSubscriptionEmail={setSubscriptionEmail}
  subscriptionStatus={subscriptionStatus}
  onSubmit={handleSubscriptionSubmit}
/>

    </div>
  );
};


export default CourseLandingPage;
